import { useState, useRef } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Alert, AlertDescription, AlertTitle } from '@creanizer/ui'
import { Button, Input } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Progress } from '@creanizer/ui'
import { AlertCircle, Package, Save, Globe, Users, Sword } from 'lucide-react'
import { useGenerator } from '../../../../hooks/useGenerator'
import { useStreamGenerator } from '../../../../hooks/useStreamGenerator'
import { useSettingsStore } from '../../../../stores/settingsStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { generateWorldContextText, generateCharacterContext, generateItemContext, generateEnemyContext } from '@creanizer/utils'
import type { ItemInput, ItemResult, ItemLockedSections } from '@creanizer/types'
import { ItemForm } from './ItemForm'
import { ItemFormSettings } from './ItemFormSettings'
import { SavedItemsList } from './SavedItemsList'
import { ItemGeneratorBatch } from './ItemGeneratorBatch'

export function ItemGenerator() {
  const { isApiKeySet } = useSettingsStore()
  const { items, addItem, addBatchItems } = useItemStore()
  const { currentWorld, savedWorlds } = useWorldBuilderStore()
  const { characters } = useCharacterStore()
  const { enemies } = useEnemyStore()
  const generator = useGenerator('item')
  const streamGenerator = useStreamGenerator('item')
  const [activeTab, setActiveTab] = useState('generate')
  const [selectedWorldId, setSelectedWorldId] = useState<string>('')
  const [selectedCharacterId, setSelectedCharacterId] = useState<string>('')
  const [ownershipType, setOwnershipType] = useState<string>('')
  const [selectedItemId, setSelectedItemId] = useState<string>('')
  const [itemRelationshipType, setItemRelationshipType] = useState<string>('')
  const [selectedEnemyId, setSelectedEnemyId] = useState<string>('')
  const [enemyRelationshipType, setEnemyRelationshipType] = useState<string>('')
  const [useStreaming, setUseStreaming] = useState<boolean>(true)
  
  // 部分再生成用の状態
  const [partialRegenerateMode, setPartialRegenerateMode] = useState<boolean>(false)
  const [existingResult, setExistingResult] = useState<ItemResult | null>(null)
  const [lockedSections, setLockedSections] = useState<ItemLockedSections>({})
  
  const [input, setInput] = useState<ItemInput>({
    mode: 'standard',
    itemType: 'equipment',
    rarity: 'common',
    genre: 'fantasy',
    techLevel: 'medieval',
    powerLevel: 5,
    creativityLevel: 7,
    numberOfItems: 5,
    batchPattern: 'random',
    rarityDistribution: 'auto',
    // 世界観連携
    worldContext: currentWorld ? JSON.stringify({
      name: currentWorld.result.name,
      parameters: currentWorld.result.parameters,
      genre: currentWorld.result.genres
    }) : undefined
  })
  
  const [results, setResults] = useState<ItemResult[]>([])
  const [error, setError] = useState<string>('')
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null)
  const resultRef = useRef<HTMLDivElement>(null)
  
  const handleGenerate = async (retryCount = 0) => {
    // 部分再生成モードの場合は、既存の結果とロックセクションを入力に含める
    const finalInput = partialRegenerateMode && existingResult 
      ? { 
          ...input, 
          existingResult, 
          lockedSections 
        }
      : input
      
    const actualInput = finalInput
    if (!isApiKeySet()) {
      setError('APIキーが設定されていません。設定ページから設定してください。')
      return
    }
    
    setError('')
    setResults([])
    setBatchProgress(null)
    
    try {
      // バッチモードで5個以上の場合はチャンク処理
      if (input.mode === 'batch' && (input.numberOfItems || 5) > 5) {
        const totalItems = input.numberOfItems || 5
        const chunkSize = 5
        const chunks = Math.ceil(totalItems / chunkSize)
        const allResults: ItemResult[] = []
        
        for (let i = 0; i < chunks; i++) {
          const currentChunkSize = Math.min(chunkSize, totalItems - (i * chunkSize))
          const currentChunkInput = {
            ...input,
            numberOfItems: currentChunkSize
          }
          
          setBatchProgress({ current: i * chunkSize + 1, total: totalItems })
          setError(`生成中... (${i * chunkSize + 1}-${Math.min((i + 1) * chunkSize, totalItems)}/${totalItems}個)`)
          
          try {
            if (useStreaming) {
              await new Promise<void>((resolve, reject) => {
                streamGenerator.generateStream(actualInput.mode === 'batch' ? currentChunkInput : actualInput, {
                  onChunk: (chunk) => {
                    console.log(`Chunk ${i + 1}/${chunks} - Received:`, chunk.length)
                  },
                  onProgress: (bytes) => {
                    console.log(`Chunk ${i + 1}/${chunks} - Progress:`, bytes)
                  },
                  onComplete: (resultData) => {
                    if (resultData !== null && resultData !== undefined) {
                      const rawResults = Array.isArray(resultData) ? resultData : [resultData]
                      const validResults = rawResults.filter(item => 
                        item && item.basicInfo && item.basicInfo.name
                      )
                      
                      if (validResults.length > 0) {
                        allResults.push(...(validResults as ItemResult[]))
                        setResults([...allResults])
                      }
                      resolve()
                    } else {
                      reject(new Error('チャンクの生成に失敗しました'))
                    }
                  },
                  onError: (error) => {
                    console.error(`Chunk ${i + 1}/${chunks} - Error:`, error)
                    reject(error)
                  }
                })
              })
            } else {
              const response = await generator.mutateAsync(actualInput.mode === 'batch' ? currentChunkInput : actualInput)
              const resultData = response.result || response
              
              if (resultData) {
                const rawResults = Array.isArray(resultData) ? resultData : [resultData]
                const validResults = rawResults.filter(item => 
                  item && item.basicInfo && item.basicInfo.name
                )
                
                if (validResults.length > 0) {
                  allResults.push(...validResults)
                  setResults([...allResults])
                }
              }
            }
            
            // 次のチャンクまで少し待機（APIレート制限対策）
            if (i < chunks - 1) {
              await new Promise(resolve => setTimeout(resolve, 1000))
            }
          } catch (chunkError) {
            console.error(`Chunk ${i + 1} failed:`, chunkError)
            // チャンクが失敗しても、既に取得した結果は保持
            if (allResults.length > 0) {
              setError(`${allResults.length}個のみ生成できました（${totalItems}個中）`)
            } else {
              throw chunkError
            }
            break
          }
        }
        
        setBatchProgress(null)
        if (allResults.length > 0) {
          setError('')
          setTimeout(() => {
            resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }, 100)
        } else {
          setError('アイテムの生成に失敗しました')
        }
        
        return
      }
      
      // 通常の処理（5個以下または単体生成）
      if (useStreaming) {
        await streamGenerator.generateStream(actualInput, {
          onChunk: (chunk) => {
            console.log('Received chunk:', chunk.length)
          },
          onProgress: (bytes) => {
            console.log('Progress:', bytes)
          },
          onComplete: (resultData) => {
            if (resultData !== null && resultData !== undefined) {
              if (Array.isArray(resultData) && resultData.length === 0) {
                console.error('No items could be recovered from incomplete data')
                setError('データの取得に失敗しました。生成数を減らすか、再度お試しください。')
                return
              }
              
              const rawResults = Array.isArray(resultData) ? resultData : [resultData]
              const validResults = rawResults.filter(item => 
                item && item.basicInfo && item.basicInfo.name
              )
              
              if (validResults.length === 0) {
                console.error('No valid items in results')
                setError('有効なアイテムデータが取得できませんでした。')
                return
              }
              
              setResults(validResults as ItemResult[])
              
              if (input.mode === 'batch' && Array.isArray(resultData)) {
                const expectedCount = input.numberOfItems || 5
                if (validResults.length > 0 && validResults.length < expectedCount) {
                  console.warn(`Partial recovery: ${validResults.length}/${expectedCount} items recovered`)
                  setError(`${validResults.length}個のみ取得できました（${expectedCount}個中）`)
                }
              }
              
              setTimeout(() => {
                resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }, 100)
            } else {
              console.error('Incomplete result received:', resultData)
              setError('結果のパースに失敗しました')
            }
          },
          onError: (error) => {
            console.error('Streaming error:', error)
            setError(error.message)
          }
        })
      } else {
        const response = await generator.mutateAsync(actualInput)
        const resultData = response.result || response
        
        if (!resultData) {
          throw new Error('生成結果が空です。APIが混雑している可能性があります。')
        }
        
        const rawResults = Array.isArray(resultData) ? resultData : [resultData]
        const validResults = rawResults.filter(item => 
          item && item.basicInfo && item.basicInfo.name
        )
        
        if (validResults.length === 0) {
          throw new Error('有効なアイテムデータが生成されませんでした。')
        }
        
        setResults(validResults)
        
        setTimeout(() => {
          resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    } catch (err) {
      console.error('Generation error:', err)
      const errorMessage = err instanceof Error ? err.message : '生成中にエラーが発生しました'
      
      if (retryCount < 3 && errorMessage.includes('空')) {
        console.log(`Retrying generation (attempt ${retryCount + 1}/3)...`)
        setError(`生成に失敗しました。再試行中... (${retryCount + 1}/3)`)
        
        const waitTime = Math.pow(2, retryCount) * 1000
        setTimeout(() => {
          handleGenerate(retryCount + 1)
        }, waitTime)
      } else {
        setError(errorMessage)
      }
    }
  }
  
  const handleSave = (result: ItemResult, index?: number) => {
    const title = result.basicInfo.name
    addItem({
      title,
      input,
      result,
      worldId: currentWorld?.id
    })
  }
  
  const handleSaveAll = () => {
    if (results.length === 0) return
    
    const itemsToSave = results.map(result => ({
      title: result.basicInfo.name,
      input,
      result,
      worldId: currentWorld?.id
    }))
    
    addBatchItems(itemsToSave)
  }
  
  const handleLoadSaved = (item: { input: ItemInput; result: ItemResult }) => {
    setInput(item.input)
    setResults([item.result])
    setExistingResult(item.result)
    setPartialRegenerateMode(true)
    // 初期状態：変更不可の項目のみロック、他は全てアンロック
    setLockedSections({
      basicInfo: {
        name: true,
        type: true,
        rarity: true
      }
    })
    setActiveTab('generate')
  }
  
  const isSaved = (result: ItemResult) => {
    if (!result || !result.basicInfo) return false
    return items.some(item => 
      item?.result?.basicInfo?.name === result.basicInfo.name &&
      item?.result?.basicInfo?.type === result.basicInfo.type
    )
  }
  
  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Package className="h-5 w-5" />
          <h2 className="text-xl font-semibold">アイテムジェネレータ</h2>
        </div>
        <p className="text-muted-foreground mb-4">
          日用品から武器防具まで、多様なアイテムを生成します
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="generate">生成</TabsTrigger>
          <TabsTrigger value="saved">
            保存済み ({items.length}/500)
          </TabsTrigger>
        </TabsList>

        <TabsContent value="generate" className="space-y-6">
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">生成設定</h3>
            
            {/* 世界観選択 */}
            {savedWorlds.length > 0 && (
              <div className="mb-4 p-4 border rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="h-4 w-4" />
                  <Label>世界観を選択（オプション）</Label>
                </div>
                <Select
                  value={selectedWorldId || 'none'}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setSelectedWorldId('')
                      setInput({ 
                        ...input, 
                        worldContext: undefined,
                        genre: 'fantasy',
                        techLevel: 'medieval'
                      })
                    } else {
                      setSelectedWorldId(value)
                      const world = savedWorlds.find(w => w.id === value)
                      if (world) {
                        const worldContext = generateWorldContextText(world.result)
                        setInput({
                          ...input,
                          worldContext: worldContext,
                          genre: undefined,
                          techLevel: undefined
                        })
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="世界観を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">選択しない</SelectItem>
                    {savedWorlds.map((world) => (
                      <SelectItem key={world.id} value={world.id}>
                        {world.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* 関連キャラクター選択 */}
            {characters.length > 0 && (
              <div className="mb-4 p-4 border rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4" />
                  <Label>関連キャラクター（オプション）</Label>
                </div>
                <Select
                  value={selectedCharacterId || 'none'}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setSelectedCharacterId('')
                      setOwnershipType('')
                      setInput({ ...input, relatedCharacterContext: undefined, ownershipType: undefined })
                    } else {
                      setSelectedCharacterId(value)
                      const character = characters.find(c => c.id === value)
                      if (character) {
                        const characterContext = generateCharacterContext(character)
                        setInput({ ...input, relatedCharacterContext: characterContext })
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="キャラクターを選択">
                      {selectedCharacterId ? (
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          {characters.find(c => c.id === selectedCharacterId)?.title}
                        </div>
                      ) : (
                        'キャラクターを選択...'
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">選択しない</SelectItem>
                    {characters.map((character) => (
                      <SelectItem key={character.id} value={character.id}>
                        <div className="flex flex-col">
                          <div className="font-medium">{character.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {character.result.name} - {character.result.role}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedCharacterId && (
                  <>
                    <div className="mt-2">
                      <Label htmlFor="ownershipType">所有関係（任意）</Label>
                      <Input
                        id="ownershipType"
                        value={ownershipType}
                        onChange={(e) => {
                          setOwnershipType(e.target.value)
                          setInput({ ...input, ownershipType: e.target.value || undefined })
                        }}
                        placeholder="例：愛用品、形見、授かり物、戦利品など（未記入の場合はAIにお任せ）"
                      />
                    </div>
                    <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                      <div className="font-medium text-blue-900 mb-1">選択中のキャラクター</div>
                      <div className="text-blue-700">
                        {characters.find(c => c.id === selectedCharacterId)?.result.summary?.substring(0, 100)}...
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* 関連エネミー選択 */}
            {enemies.length > 0 && (
              <div className="mb-4 p-4 border rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Sword className="h-4 w-4" />
                  <Label>関連エネミー（オプション）</Label>
                </div>
                <Select
                  value={selectedEnemyId || 'none'}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setSelectedEnemyId('')
                      setEnemyRelationshipType('')
                      setInput({ ...input, relatedEnemyContext: undefined, enemyRelationshipType: undefined })
                    } else {
                      setSelectedEnemyId(value)
                      const enemy = enemies.find(e => e.id === value)
                      if (enemy) {
                        const enemyContext = generateEnemyContext(enemy)
                        setInput({ ...input, relatedEnemyContext: enemyContext })
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="エネミーを選択">
                      {selectedEnemyId ? (
                        <div className="flex items-center gap-2">
                          <Sword className="h-4 w-4" />
                          {enemies.find(e => e.id === selectedEnemyId)?.title}
                        </div>
                      ) : (
                        'エネミーを選択...'
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">選択しない</SelectItem>
                    {enemies.map((enemy) => (
                      <SelectItem key={enemy.id} value={enemy.id}>
                        <div className="flex flex-col">
                          <div className="font-medium">{enemy.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {enemy.result.basicInfo.type} - {enemy.result.basicInfo.threat}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedEnemyId && (
                  <>
                    <div className="mt-2">
                      <Label htmlFor="enemyRelationshipType">エネミーとの関係（任意）</Label>
                      <Input
                        id="enemyRelationshipType"
                        value={enemyRelationshipType}
                        onChange={(e) => {
                          setEnemyRelationshipType(e.target.value)
                          setInput({ ...input, enemyRelationshipType: e.target.value || undefined })
                        }}
                        placeholder="例：ドロップ品、素材、対抗装備、専用武器など（未記入の場合はAIにお任せ）"
                      />
                    </div>
                    <div className="mt-2 p-2 bg-red-50 rounded text-xs">
                      <div className="font-medium text-red-900 mb-1">選択中のエネミー</div>
                      <div className="text-red-700">
                        {enemies.find(e => e.id === selectedEnemyId)?.result.basicInfo.summary?.substring(0, 100)}...
                      </div>
                      <div className="text-red-600 mt-1">
                        ※ このエネミーのドロップ品、素材、対抗装備として適したアイテムが生成されます
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* 関連アイテム選択 */}
            {items.length > 0 && (
              <div className="mb-4 p-4 border rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="h-4 w-4" />
                  <Label>関連アイテム（オプション）</Label>
                </div>
                <Select
                  value={selectedItemId || 'none'}
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setSelectedItemId('')
                      setItemRelationshipType('')
                      setInput({ ...input, relatedItemContext: undefined, itemRelationshipType: undefined })
                    } else {
                      setSelectedItemId(value)
                      const item = items.find(i => i.id === value)
                      if (item) {
                        const itemContext = generateItemContext(item)
                        setInput({ ...input, relatedItemContext: itemContext })
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="アイテムを選択">
                      {selectedItemId ? (
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          {items.find(i => i.id === selectedItemId)?.title}
                        </div>
                      ) : (
                        'アイテムを選択...'
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">選択しない</SelectItem>
                    {items.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        <div className="flex flex-col">
                          <div className="font-medium">{item.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {item.result.basicInfo.type} - {item.result.basicInfo.rarity}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedItemId && (
                  <>
                    <div className="mt-2">
                      <Label htmlFor="itemRelationshipType">アイテム間の関係（任意）</Label>
                      <Input
                        id="itemRelationshipType"
                        value={itemRelationshipType}
                        onChange={(e) => {
                          setItemRelationshipType(e.target.value)
                          setInput({ ...input, itemRelationshipType: e.target.value || undefined })
                        }}
                        placeholder="例：対の剣、アップグレード版、セットアイテム、材料など（未記入の場合はAIにお任せ）"
                      />
                    </div>
                    <div className="mt-2 p-2 bg-purple-50 rounded text-xs">
                      <div className="font-medium text-purple-900 mb-1">選択中のアイテム</div>
                      <div className="text-purple-700">
                        {items.find(i => i.id === selectedItemId)?.result.basicInfo.summary?.substring(0, 100)}...
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            
            <ItemForm
              input={input}
              onChange={setInput}
              onGenerate={() => handleGenerate()}
              isGenerating={generator.isPending || streamGenerator.isStreaming}
              partialRegenerateMode={partialRegenerateMode}
              existingResult={existingResult}
              lockedSections={lockedSections}
              onLockedSectionsChange={setLockedSections}
              onPartialRegenerateModeChange={setPartialRegenerateMode}
            />
            
            <ItemFormSettings
              input={input}
              onChange={setInput}
              useStreaming={useStreaming}
              onStreamingChange={setUseStreaming}
              mode={input.mode}
              hasWorldContext={!!selectedWorldId && selectedWorldId !== 'none'}
            />
            
            <div className="flex justify-end mt-4">
              <Button
                onClick={() => handleGenerate()}
                disabled={generator.isPending || streamGenerator.isStreaming}
              >
                {generator.isPending || streamGenerator.isStreaming ? '生成中...' : 'アイテムを生成'}
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>エラー</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {(streamGenerator.isStreaming || batchProgress) && (
            <div className="card">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>生成中...</span>
                  <span>{Math.round(streamGenerator.progress)}%</span>
                </div>
                <Progress value={streamGenerator.progress} />
                {streamGenerator.partialContent && (
                  <div className="mt-4 p-4 bg-muted rounded max-h-48 overflow-y-auto">
                    <pre className="text-xs whitespace-pre-wrap">
                      {streamGenerator.partialContent.slice(-500)}
                      {streamGenerator.partialContent.length > 500 && '...'}
                    </pre>
                  </div>
                )}
                {input.mode === 'batch' && (
                  <div className="mt-2 space-y-1">
                    <p className="text-sm text-muted-foreground">
                      バッチモードで{input.numberOfItems || 5}個のアイテムを生成中...
                    </p>
                    {batchProgress && (
                      <p className="text-sm font-medium">
                        進捗: {batchProgress.current}/{batchProgress.total}個
                      </p>
                    )}
                    {(input.numberOfItems || 5) > 5 && (
                      <p className="text-xs text-muted-foreground">
                        ※ {Math.ceil((input.numberOfItems || 5) / 5)}回のAPIを使用して生成します
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="saved">
          <SavedItemsList
            onLoad={handleLoadSaved}
          />
        </TabsContent>
      </Tabs>
      
      {results.length > 0 && (
        <div className="card" ref={resultRef}>
          <div className="flex flex-row items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              生成結果
              {results.length > 1 && ` (${results.length}個)`}
            </h3>
            {results.length > 1 && (
              <button
                onClick={handleSaveAll}
                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
              >
                <Save className="h-4 w-4 mr-2" />
                すべて保存
              </button>
            )}
          </div>
          <ItemGeneratorBatch
            results={results}
            onSave={handleSave}
            onSaveAll={handleSaveAll}
            isSaved={isSaved}
            onEdit={(updatedResult, index) => {
              const updatedResults = [...results]
              updatedResults[index] = { ...results[index], ...updatedResult }
              setResults(updatedResults)
            }}
          />
        </div>
      )}
    </div>
  )
}